import { useState } from 'react';
import {
  Box, Grid2, Typography, Switch, Card, CardContent, Divider, Accordion, AccordionSummary, AccordionDetails, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import CardHeader from '@mui/material/CardHeader';
import SvgIcon from '@mui/material/SvgIcon';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import { BaseTooltip } from '@mgm/common/components/base';
import { SettingsWrapper } from '../../../layout';
import { SettingsHeader } from './SettingsHeader';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getManagerPermissions, getEmployeePermissions } from '../../../store/settings/selectors';
import { ReactComponent as InfoIcon } from '../../../static/icons/ic_information.svg';
import { useUpdateEmployeePermissions, useUpdateManagerPermissions } from '../../../hooks/api/useSettings';
import { getTokenDetails } from '../../../store/authenticate/selectors';

const PermissionSettings = () => {
  const { t } = useTranslation(['common', 'nav', 'settings']);
  const managerPermissions = useAppSelector(getManagerPermissions);
  const employeePermissions = useAppSelector(getEmployeePermissions);
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: managerMutate, isLoading: isManagerLoading } = useUpdateManagerPermissions();
  const { mutateAsync: employeeMutate, isLoading: isEmploeeLoading } = useUpdateEmployeePermissions();
  const tokenDetails = useAppSelector(getTokenDetails);

  const [permissions, setPermissions] = useState<any>({
    manager: { ...managerPermissions },
    employee: { ...employeePermissions },
  });

  const [isBtnDisabled, setIsBtnDisabled] = useState(true);

  // State to manage the expanded state of all accordions
  const [expandedManager, setExpandedManager] = useState<string[]>([]);
  const [expandedEmployee, setExpandedEmployee] = useState<string[]>([]);

  const handleToggle = (role: string, type: string, category: string, key: string) => {
    setPermissions((prevPermissions: any) => ({
      ...prevPermissions,
      [role]: {
        ...prevPermissions[role],
        [type]: {
          ...prevPermissions[role][type],
          [category]: {
            ...prevPermissions[role][type][category],
            [key]: !prevPermissions[role][type][category][key],
          },
        },
      },
    }));
    setIsBtnDisabled(false);
  };

  const savePermissions = async () => {
    await managerMutate({ ...permissions.manager });
    await employeeMutate({ ...permissions.employee });

    enqueueSnackbar(`${t('permissionsWasUpdated', { ns: 'settings' })}`, { variant: 'success' });
  };

  const renderSwitch = (role: string, type: string, categoryKey: string, key: string, value: any, isDisabled = false) => {
    const shouldShowTooltip = !(key === 'manage' && categoryKey.startsWith('pos')); // Condition to hide tooltip and icon

    return (
      <Box
        key={key}
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: tokenDetails.settings.ui?.theme === 'light' ? '#F4F6F8' : '',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body2">
            {t(`${role}.${categoryKey}.${type}.${key}.text`, { ns: 'settings' })}
          </Typography>
          {shouldShowTooltip && ( // Conditionally render tooltip and icon
            <BaseTooltip tooltipText={t(`${role}.${categoryKey}.${type}.${key}.tooltip`, { ns: 'settings' })}>
              <SvgIcon
                sx={{
                  color: '#A855F7',
                }}
                component={InfoIcon}
                inheritViewBox
              />
            </BaseTooltip>
          )}
        </Stack>
        <Switch
          checked={Boolean(value)}
          onChange={() => handleToggle(role, type, categoryKey, key)}
          color="primary"
          disabled={isDisabled}
        />
      </Box>
    );
  };

  const toggleAllAccordions = (role: string, action: 'expand' | 'collapse') => {
    const allPages = Object.keys(permissions[role].visibility);

    if (action === 'expand') {
      if (role === 'manager') setExpandedManager(allPages);
      else setExpandedEmployee(allPages);
    } else if (role === 'manager') setExpandedManager([]);
    else setExpandedEmployee([]);
  };

  const renderPermissionSection = (role: string) => {
    const visibilityData = permissions[role].visibility;
    const actionsData = permissions[role].actions;

    // Merge the keys from both visibilityData and actionsData for manager, for employee only use visibility
    const allPages = new Set([
      ...Object.keys(visibilityData),
      ...(role === 'manager' ? Object.keys(actionsData) : []),
    ]);

    const expanded = role === 'manager' ? expandedManager : expandedEmployee;
    const setExpanded = role === 'manager' ? setExpandedManager : setExpandedEmployee;

    return (
      <>
        {[...allPages].map((categoryKey) => {
          // Dynamically check if the "view" field is false to disable other switches
          const isViewDisabled = role === 'manager' && visibilityData[categoryKey]?.view === false;

          const filteredVisibilityData = role === 'employee' && visibilityData[categoryKey]
            ? { view: visibilityData[categoryKey].view }
            : visibilityData[categoryKey];

          const hasVisibilityData = filteredVisibilityData && Object.keys(filteredVisibilityData).length > 0;
          const hasActionsData = role === 'manager' && actionsData[categoryKey] && Object.keys(actionsData[categoryKey]).length > 0;

          if (hasVisibilityData || hasActionsData) {
            return (
              <Accordion
                key={categoryKey}
                expanded={expanded.includes(categoryKey)}
                onChange={() => {
                  setExpanded((prev) => (prev.includes(categoryKey)
                    ? prev.filter((item) => item !== categoryKey)
                    : [...prev, categoryKey]));
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">{t(`${categoryKey}`, { ns: 'nav' })}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid2 container alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Grid2 size={{ xs: 12 }}>
                      {/* Render visibility switches, disabling based on the view field */}
                      {hasVisibilityData
                        && Object.entries(filteredVisibilityData).map(([key, value]) => {
                          const isDisabled = key !== 'view' && isViewDisabled; // Disable non-view fields if view is false
                          return renderSwitch(role, 'visibility', categoryKey, key, value, isDisabled);
                        })}

                      {/* Render actions switches for manager, disabling if view is false */}
                      {hasActionsData
                        && Object.entries(actionsData[categoryKey])
                          .map(([key, value]) => renderSwitch(role, 'actions', categoryKey, key, value, isViewDisabled))}
                    </Grid2>
                  </Grid2>
                </AccordionDetails>
              </Accordion>
            );
          }
          return null;
        })}
      </>
    );
  };

  const renderPermissionsCard = (role: string, title: string) => (
    <Card sx={{
      mb: 3, p: 1, borderRadius: 2, ':hover': { boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px' },
    }}
    >
      <CardHeader
        sx={{ p: 2 }}
        title={title}
        titleTypographyProps={{ variant: 'h6', color: 'primary' }}
        action={(
          <Box>
            <Button onClick={() => toggleAllAccordions(role, 'expand')}>{t('showAll', { ns: 'settings' })}</Button>
            <Button onClick={() => toggleAllAccordions(role, 'collapse')}>{t('hideAll', { ns: 'settings' })}</Button>
          </Box>
        )}
      />
      <CardContent>{renderPermissionSection(role)}</CardContent>
    </Card>
  );

  return (
    <SettingsWrapper>
      <SettingsHeader title={t('permissions', { ns: 'inputs' })} subtitle={t('manageWhatYourTeam', { ns: 'settings' })} />
      <Divider sx={{ pt: 2, mb: 4, borderStyle: 'dashed' }} />
      <Box>
        {renderPermissionsCard('manager', t('manager'))}
        {renderPermissionsCard('employee', t('employee'))}

        <Box sx={{ textAlign: 'right', pb: 3 }}>
          <LoadingButton
            loading={isManagerLoading || isEmploeeLoading}
            onClick={savePermissions}
            disabled={isBtnDisabled}
            variant="contained"
            color="primary"
            size="large"
          >
            {t('saveChanges', { ns: 'settings' })}
          </LoadingButton>
        </Box>
      </Box>
    </SettingsWrapper>
  );
};

export { PermissionSettings };
